import React, { useRef, useEffect } from 'react';
import { Transformer, Group } from 'react-konva';
import { GroupElement, LabelElement } from '../../types';
import ChildRenderer from './ChildRenderer';
import { SalesOrder } from 'services/salesOrders';

interface GroupRendererProps {
  group: GroupElement;
  isSelected: boolean;
  onSelect: () => void;
  onChange: (newAttrs: Partial<LabelElement>) => void;
  previewMode?: boolean;
  previewData: SalesOrder | null;
  gridSize: number;
}

const GroupRenderer: React.FC<GroupRendererProps> = ({
  group,
  isSelected,
  onSelect,
  onChange,
  previewMode = false,
  previewData,
  gridSize,
}) => {
  const shapeRef = useRef<any>(null);
  const transformerRef = useRef<any>(null);

  const snapToGrid = (value: number): number => {
    return Math.round(value / gridSize) * gridSize;
  };

  useEffect(() => {
    if (isSelected && transformerRef.current && shapeRef.current) {
      transformerRef.current.nodes([shapeRef.current]);
      transformerRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  const handleDragEnd = (e: any) => {
    const node = e.target;
    onChange({
      x: snapToGrid(node.x()),
      y: snapToGrid(node.y()),
    });
  };

  const handleTransformEnd = () => {
    const node = shapeRef.current;

    onChange({
      x: snapToGrid(node.x()),
      y: snapToGrid(node.y()),
      rotation: Math.round(node.rotation() / 15) * 15,
    });
  };

  const commonProps = {
    ref: shapeRef,
    x: group.x,
    y: group.y,
    width: group.width,
    height: group.height,
    rotation: group.rotation || 0,
    draggable: !previewMode,
    onClick: onSelect,
    onTap: onSelect,
    onDragEnd: handleDragEnd,
    onTransformEnd: handleTransformEnd,
    onMouseEnter: (e: any) => {
      if (previewMode) return;
      const container = e.target.getStage().container();
      container.style.cursor = 'move';
    },
    onMouseLeave: (e: any) => {
      if (previewMode) return;
      const container = e.target.getStage().container();
      container.style.cursor = 'default';
    },
  };

  return (
    <>
      {
        <Group {...commonProps}>
          {group.children.map((child) => (
            <ChildRenderer
              key={child.id}
              child={child}
              previewMode={previewMode}
              previewData={previewData}
            />
          ))}
        </Group>
      }
      {isSelected && !previewMode && (
        <Transformer
          ref={transformerRef}
          boundBoxFunc={(oldBox, newBox) => {
            const snappedBox = {
              ...newBox,
              x: snapToGrid(newBox.x),
              y: snapToGrid(newBox.y),
              width: snapToGrid(newBox.width),
              height: snapToGrid(newBox.height),
            };

            if (snappedBox.width < gridSize || snappedBox.height < gridSize) {
              return oldBox;
            }
            return snappedBox;
          }}
          rotationSnaps={[
            0, 15, 30, 45, 60, 75, 90, 105, 120, 135, 150, 165, 180,
          ]}
          enabledAnchors={[]}
          rotateEnabled={true}
          shouldOverdrawWholeArea
          onMouseEnter={(e) => {
            if (previewMode) return;
            const container = e.target.getStage()?.container();
            container && (container.style.cursor = 'move');
          }}
          onMouseLeave={(e) => {
            if (previewMode) return;
            const container = e.target.getStage()?.container();
            container && (container.style.cursor = 'default');
          }}
        />
      )}
    </>
  );
};

export default GroupRenderer;
